import React, { useState } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { FiFile, FiDownload, FiX, FiCalendar } from 'react-icons/fi';

function formatDate(dateStr) {
  if (!dateStr) return '';
  const d = new Date(dateStr);
  if (isNaN(d.getTime())) return '';
  return d.toLocaleDateString('en-AU', { day: '2-digit', month: 'short', year: 'numeric' });
}

function DownloadButton({ href, filename }) {
  const handleDownload = async (e) => {
    e.preventDefault();
    e.stopPropagation();
    try {
      const response = await fetch(href);
      const blob = await response.blob();
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = filename || 'download';
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
    } catch {
      window.open(href, '_blank');
    }
  };

  return (
    <a
      href={href}
      onClick={handleDownload}
      title="Download"
      style={{
        display: 'flex', alignItems: 'center', justifyContent: 'center',
        width: 30, height: 30, borderRadius: 7,
        background: 'var(--bg-card)', border: '1px solid var(--border)',
        color: 'var(--text-secondary)', cursor: 'pointer', flexShrink: 0,
        textDecoration: 'none', transition: 'background 0.15s'
      }}
      onMouseEnter={e => { e.currentTarget.style.background = '#2563eb'; e.currentTarget.style.color = '#fff'; e.currentTarget.style.borderColor = '#2563eb'; }}
      onMouseLeave={e => { e.currentTarget.style.background = 'var(--bg-card)'; e.currentTarget.style.color = 'var(--text-secondary)'; e.currentTarget.style.borderColor = 'var(--border)'; }}
    >
      <FiDownload size={14} />
    </a>
  );
}

function FileCaption({ file, name, dateStr, queryUrl }) {
  const displayName = file.description || file.original_name || file.file;
  const fileName = file.original_name || file.file;
  const showFileName = file.description && fileName !== file.description;

  return (
    <div style={{ padding: '8px 10px', display: 'flex', alignItems: 'center', gap: 8 }}>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{
          fontSize: 13, fontWeight: 600, color: 'var(--text-primary)',
          lineHeight: 1.3, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap'
        }}>
          {displayName}
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: 6, marginTop: 3 }}>
          {showFileName && (
            <span style={{
              fontSize: 11, color: 'var(--text-muted)',
              overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', maxWidth: '55%'
            }}>
              {fileName}
            </span>
          )}
          {dateStr && (
            <span style={{ fontSize: 11, color: 'var(--text-muted)', display: 'flex', alignItems: 'center', gap: 3, flexShrink: 0 }}>
              {showFileName && <span style={{ opacity: 0.4 }}>·</span>}
              <FiCalendar size={10} style={{ opacity: 0.5 }} />
              {dateStr}
            </span>
          )}
        </div>
      </div>
      <DownloadButton href={queryUrl} filename={file.original_name || file.file} />
    </div>
  );
}

function ImageLightbox({ src, alt, onClose }) {
  return (
    <motion.div
      initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
      onClick={onClose}
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.85)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 300,
        cursor: 'zoom-out'
      }}
    >
      <button onClick={onClose} style={{
        position: 'absolute', top: 16, right: 16, background: 'rgba(255,255,255,0.15)',
        border: 'none', color: '#fff', borderRadius: '50%', width: 40, height: 40,
        display: 'flex', alignItems: 'center', justifyContent: 'center', cursor: 'pointer'
      }}><FiX size={20} /></button>
      <motion.div initial={{ scale: 0.9 }} animate={{ scale: 1 }} onClick={e => e.stopPropagation()} style={{ cursor: 'default' }}>
        <img
          src={src}
          alt={alt}
          style={{ maxWidth: '90vw', maxHeight: '90vh', borderRadius: 8, objectFit: 'contain' }}
        />
      </motion.div>
    </motion.div>
  );
}

export default function MediaViewer({ file, token }) {
  const [lightbox, setLightbox] = useState(false);
  const [imgError, setImgError] = useState(false);

  const queryUrl = file.url + (token ? `?token=${token}` : '');
  const name = file.description || file.original_name || file.file;
  const ext = (file.ext || '').toLowerCase();
  const isImage = file.isImage || ['.jpg', '.jpeg', '.png', '.gif', '.webp', '.bmp', '.svg', '.heic', '.heif', '.tiff'].includes(ext);
  const isVideo = file.isVideo || ['.mp4', '.webm', '.ogg', '.mov', '.avi'].includes(ext);
  const dateStr = formatDate(file.created_at);

  const cardStyle = {
    width: '100%', maxWidth: 220, minWidth: 140, borderRadius: 10, overflow: 'hidden',
    border: '1px solid var(--border)', background: 'var(--bg-input)'
  };

  if (isImage) {
    return (
      <>
        <div style={cardStyle}>
          {imgError ? (
            <div style={{
              width: '100%', height: 160, display: 'flex', alignItems: 'center', justifyContent: 'center',
              background: 'var(--bg-input)', color: 'var(--text-muted)', fontSize: 12
            }}>
              <FiFile size={24} style={{ marginRight: 8, opacity: 0.4 }} /> Image unavailable
            </div>
          ) : (
            <motion.div
              whileHover={{ scale: 1.02 }}
              onClick={() => setLightbox(true)}
              style={{ cursor: 'zoom-in' }}
            >
              <img
                src={queryUrl}
                alt={name}
                style={{ width: '100%', height: 160, objectFit: 'cover', display: 'block' }}
                onError={() => setImgError(true)}
              />
            </motion.div>
          )}
          <FileCaption file={file} name={name} dateStr={dateStr} queryUrl={queryUrl} />
        </div>
        <AnimatePresence>
          {lightbox && (
            <ImageLightbox
              src={queryUrl}
              alt={name}
              onClose={() => setLightbox(false)}
            />
          )}
        </AnimatePresence>
      </>
    );
  }

  if (isVideo) {
    return (
      <div style={{ ...cardStyle, maxWidth: 280 }}>
        <video controls preload="metadata" style={{ width: '100%', display: 'block' }}>
          <source src={queryUrl} />
        </video>
        <FileCaption file={file} name={name} dateStr={dateStr} queryUrl={queryUrl} />
      </div>
    );
  }

  // Document / other file — tappable to open in new tab
  return (
    <div
      onClick={() => window.open(queryUrl, '_blank')}
      style={{ ...cardStyle, cursor: 'pointer' }}
    >
      <div style={{
        height: 80, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 8,
        background: 'var(--bg-card)'
      }}>
        <FiFile size={28} color="var(--text-secondary)" />
        <span style={{ fontSize: 12, fontWeight: 700, color: 'var(--text-muted)', textTransform: 'uppercase' }}>
          {ext.replace('.', '') || 'FILE'}
        </span>
      </div>
      <FileCaption file={file} name={name} dateStr={dateStr} queryUrl={queryUrl} />
    </div>
  );
}
